import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Typography } from '@mui/material';
import Avatar from 'components/Avatar';
import { useClickOutside } from 'hooks/useClickOutside';
import useServiceChange from 'hooks/useServiceChange';
import store from 'store';

import { ROUTES, getRoute } from '@common/constants/CampusRoutes';
import { IStudentModel } from '@common/models/Users/Students/Student';

import {
	ArrowIcon,
	ChildLabel,
	Dropdown,
	NameRow,
	SelectWrapper,
	SelectedCard,
	SelectedContent,
	StudentItem,
	StudentMeta,
	StudentName,
} from './styled';

function SelectStudent() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { clickOutsideRef } = useClickOutside(() => setOpen(false));
	const navigate = useNavigate();
	const location = useLocation();
	const serviceChange = useServiceChange();
	const baseRoutes = useMemo(() => new Set<string>(Object.values(ROUTES)), []);

	const getBaseRoute = (pathname: string) => {
		const [segment] = pathname.split('/').filter(Boolean);
		if (!segment) return getRoute(['HOME']);

		const root = `/${segment}`;
		return baseRoutes.has(root) ? root : getRoute(['HOME']);
	};

	const students = store.profile.parent?.students || ([] as Array<IStudentModel>);

	const selectedStudent = useMemo(
		() =>
			students.find((student: IStudentModel) => student.id === store.currentStudentId) ||
			students[0],
		[students, store.currentStudentId],
	);

	useEffect(() => {
		if (students.length && !store.currentStudentId) {
			store.selectStudent(students[0].id);
		}
	}, [students, store.currentStudentId]);

	const getStudentName = (student?: IStudentModel) =>
		[student?.first_name, student?.last_name].filter(Boolean).join(' ') || student?.fullName || '';

	const getStudentSubtitle = (student?: IStudentModel) =>
		student?.groups?.[0]?.official_name || student?.groups?.[0]?.name || '';

	const handleSelectStudent = (student: IStudentModel) => {
		if (!student) return;

		const currentServiceId = store.services.current?.id;
		const studentServiceIds = (student.services_ids ?? [])
			.map((service: any) => (typeof service === 'number' ? service : service?.id))
			.filter((serviceId: any): serviceId is number => Number.isFinite(serviceId));
		const serviceId = studentServiceIds.includes(currentServiceId)
			? currentServiceId
			: studentServiceIds[0];
		if (serviceId && serviceId !== currentServiceId) {
			serviceChange(serviceId);
		}

		store.selectStudent(student.id);
		navigate(getBaseRoute(location.pathname));
		setOpen(false);
	};

	if (!selectedStudent) {
		return null;
	}

	return (
		<SelectWrapper ref={clickOutsideRef}>
			<SelectedCard onClick={() => setOpen((prev) => !prev)}>
				<Avatar
					src={selectedStudent.avatar_url || selectedStudent.avatar?.url}
					alt={getStudentName(selectedStudent)}
					size={60}
				/>
				<SelectedContent>
					<ChildLabel variant="body2">{t('shared.childAccount')}</ChildLabel>
					<NameRow>
						<Typography variant="subtitle1" component="span">
							{getStudentName(selectedStudent)}
						</Typography>
						<ArrowIcon $open={open}>
							<KeyboardArrowDownIcon />
						</ArrowIcon>
					</NameRow>
				</SelectedContent>
			</SelectedCard>

			{open && (
				<Dropdown>
					{students.map((student: IStudentModel) => (
						<StudentItem
							key={student.id}
							data-selected={student.id === selectedStudent.id}
							onClick={(event) => {
								event.stopPropagation();
								handleSelectStudent(student);
							}}
						>
							<Avatar
								src={student.avatar_url || student.avatar?.url}
								alt={getStudentName(student)}
								size={52}
							/>
							<SelectedContent>
								<StudentName>{getStudentName(student)}</StudentName>
								{getStudentSubtitle(student) && (
									<StudentMeta>{getStudentSubtitle(student)}</StudentMeta>
								)}
							</SelectedContent>
							{student.id === selectedStudent.id && <CheckIcon fontSize="small" />}
						</StudentItem>
					))}
				</Dropdown>
			)}
		</SelectWrapper>
	);
}

export default observer(SelectStudent);
